import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
} from "@mui/material";
import axios from "axios";
import { API_BASE } from "../services/api";
import type { RawAxiosRequestHeaders } from "axios";

interface ApiResponse<T = any> {
  timestamp: string;
  success: boolean;
  message: string;
  error: string;
  data: T;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void; // 생성 후 목록 리프레시 (부모에서 다이얼로그 닫기 + fetchFolders)
  parentFolderId: string | null; // 선택된 상위 폴더 (null이면 루트)
}

const FolderCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  parentFolderId,
}) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const getHeaders = (): RawAxiosRequestHeaders => ({
    "x-user-id": localStorage.getItem("user_id") || "",
    "x-access-token": localStorage.getItem("access_token") || "",
    "x-refresh-token": localStorage.getItem("refresh_token") || "",
  });

  // 다이얼로그 열릴 때마다 에러 초기화
  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleCreate = async () => {
    setError(null);
    const orgId = localStorage.getItem("org_id");

    if (!orgId) {
      setError("org_id가 없습니다. 다시 로그인해 주세요.");
      return;
    }
    if (!name.trim()) {
      setError("폴더명을 입력하세요.");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        org_id: orgId,
        parent_folder_id: parentFolderId, // 상위 폴더 (null이면 루트)
        name: name.trim(),
        description: desc.trim() || null,
      };

      const res = await axios.post<ApiResponse<{ folder_id: string }>>(
        `${API_BASE}folders`,
        payload,
        { headers: getHeaders() }
      );

      if (!res.data?.success) {
        setError(
          res.data?.error || res.data?.message || "폴더 생성에 실패했습니다."
        );
        return;
      }

      // 입력값 초기화
      setName("");
      setDesc("");

      // 부모에게 알림 (부모에서 다이얼로그 닫기 + 목록 새로고침)
      onCreated();
    } catch (e: any) {
      setError(e?.message || "요청에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return; // 생성 중일 때는 닫기 방지
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>폴더 생성</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="caption" color="text.secondary">
            {parentFolderId
              ? "선택된 폴더의 하위 폴더로 생성됩니다."
              : "루트(최상위) 폴더로 생성됩니다."}
          </Typography>

          <TextField
            label="폴더명"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            helperText="같은 상위 폴더 내에서 유일한 이름이어야 합니다."
          />
          <TextField
            label="설명 (선택)"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          취소
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={busy}>
          {busy ? "생성 중..." : "생성"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderCreateDialog;
